/* eslint-disable @repo/internal/styles/no-exported-styles */
/** @jsx jsx */
import { Children, forwardRef, Fragment, memo } from 'react';
import { css, jsx } from '@emotion/react';
import { spaceStylesMap } from '../xcss/style-maps.partial';
const alignItemsMap = {
  center: css({
    alignItems: 'center'
  }),
  baseline: css({
    alignItems: 'baseline'
  }),
  start: css({
    alignItems: 'flex-start'
  }),
  end: css({
    alignItems: 'flex-end'
  })
};
const justifyContentMap = {
  start: css({
    justifyContent: 'flex-start'
  }),
  center: css({
    justifyContent: 'center'
  }),
  end: css({
    justifyContent: 'flex-end'
  }),
  'space-between': css({
    justifyContent: 'space-between'
  })
};
const flexGrowMap = {
  hug: css({
    flexGrow: 0
  }),
  fill: css({
    width: '100%',
    flexGrow: 1
  })
};
const flexWrapStyles = css({
  flexWrap: 'wrap'
});
const baseStyles = css({
  display: 'flex',
  boxSizing: 'border-box',
  flexDirection: 'row'
});
const separatorStyles = css({
  color: "var(--ds-text-subtle, #42526E)",
  marginBlock: "var(--ds-space-0, 0px)",
  marginInline: `calc(-1 * ${"var(--ds-space-025, 2px)"})`,
  pointerEvents: 'none',
  userSelect: 'none'
});
const Separator = ({
  children
}) => jsx("span", {
  css: separatorStyles
}, children);

/**
 * __Inline__
 *
 * Inline is a primitive component based on flexbox that manages the horizontal layout of direct children.
 *
 *
 * @example
 * ```tsx
 *  <Inline>
 *    <Box padding="space.100" backgroundColor="neutral"></Box>
 *    <Box padding="space.100" backgroundColor="neutral"></Box>
 *  </Inline>
 * ```
 *
 */
const Inline = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  as,
  alignInline,
  alignBlock: alignItems,
  shouldWrap = false,
  spread,
  grow,
  space,
  rowSpace,
  separator,
  testId,
  children: rawChildren
}, ref) => {
  const Component = as || 'div';
  const separatorComponent = typeof separator === 'string' ? jsx(Separator, null, separator) : separator;
  const children = separatorComponent ? Children.toArray(rawChildren).filter(Boolean).map((child, index) => {
    return jsx(Fragment, {
      key: index
    }, separator && index > 0 ? separatorComponent : null, child);
  }) : rawChildren;
  const justifyContent = spread || alignInline;
  return jsx(Component, {
    css: [baseStyles, space && spaceStylesMap.gap[space], justifyContent && justifyContentMap[justifyContent], grow && flexGrowMap[grow], alignItems && alignItemsMap[alignItems], shouldWrap && flexWrapStyles, rowSpace && spaceStylesMap.rowGap[rowSpace]],
    "data-testid": testId,
    ref: ref
  }, children);
}));
Inline.displayName = 'Inline';
export default Inline;